<template>
  <div class="site-assignment-container">

     <!-- Header -->
     <Header title="Assignation des Sites de Pointage">
      <template #icon>
        <span class="material-icons">place</span>
      </template>
      <template #actions>
        <div class="header-actions">
          <router-link to="/shifts" class="back-button">
            <span class="material-icons">arrow_back</span>
            Retour à la liste
          </router-link>
        </div>
      </template>
    </Header>

    <!-- Contenu principal -->
    <div class="content-container" v-if="loading">
      <div class="loading-indicator">
        <div class="spinner"></div>
        <p>Chargement des données...</p>
      </div>
    </div>

    <div class="content-container" v-else>
      <!-- Filtres et contrôles -->
      <div class="controls-container">
        <div class="filters">
          <div class="filter-group">
            <label>Filtrer par direction:</label>
            <div class="select-wrapper">
              <select v-model="filterDirection" class="filter-select">
                <option value="">Toutes les directions</option>
                <option v-for="direction in directions " :key="direction.id" :value="direction.id">
                  {{ direction.nom }}
                </option>
              </select>
              <span class="select-icon material-icons">expand_more</span>
            </div>
          </div>
          
          <div class="filter-group">
            <label>Filtrer par site:</label>
            <div class="select-wrapper">
              <select v-model="filterSite" class="filter-select">
                <option value="">Tous les sites</option>
                <option v-for="site in sites" :key="site.id" :value="site.id">
                  {{ site.name }}
                </option>
              </select>
              <span class="select-icon material-icons">expand_more</span>
            </div>
          </div>
          
          <div class="filter-group">
            <label>Filtrer par agent:</label>
            <div class="search-container">
              <input 
                type="text" 
                v-model="searchQuery" 
                placeholder="Rechercher un agent..." 
                class="search-input"
              />
              <span class="material-icons search-icon">search</span>
          </div>
          </div>
          
        </div>
        
        <div class="action-buttons">
          <div class="export-dropdown" style="position: relative;">
            <button class="btn btn-secondary dropdown-toggle" @click="toggleExportMenu" ref="exportButton">
              <span class="material-icons">download</span>
              Exporter
              <span class="material-icons">arrow_drop_down</span>
            </button>
            <teleport to="body">
              <div v-if="showExportMenu" class="dropdown-menu-portal" :style="dropdownPosition" style="position: fixed; z-index: 1050; min-width: 160px; background: white; border-radius: 8px; box-shadow: 0 3px 15px rgba(0,0,0,0.2); border: 1px solid #eaeaea; overflow: hidden;">
                <button class="dropdown-item" @click="exportToExcel" style="display: flex; align-items: center; padding: 10px 15px; border: none; background: none; width: 100%; text-align: left; cursor: pointer; transition: background 0.2s;">
                  <span class="material-icons" style="margin-right: 8px;">description</span>
                  Excel
                </button>
                <button class="dropdown-item" @click="exportToPDF" style="display: flex; align-items: center; padding: 10px 15px; border: none; background: none; width: 100%; text-align: left; cursor: pointer; transition: background 0.2s;">
                  <span class="material-icons" style="margin-right: 8px;">picture_as_pdf</span>
                  PDF
                </button>
              </div>
            </teleport>
          </div>
        </div>
      </div>

      <div class="assignment-container">
        <!-- Liste du personnel disponible -->
        <div class="personnel-list-container">
          <h2>Personnel disponible</h2>
          <div class="search-and-actions">
            <div class="search-box">
              <span class="material-icons">search</span>
              <input 
                type="text" 
                v-model="personnelSearchQuery" 
                placeholder="Rechercher un agent..." 
                class="search-input"
              />
            </div>
            <div class="selection-actions" v-if="selectedPersonnel.length > 0">
              <span class="selected-count">{{ selectedPersonnel.length }} sélectionné(s)</span>
              <button class="btn btn-sm btn-secondary" @click="deselectAllAgents">
                <span class="material-icons">clear</span>
              </button>
            </div>
            <div class="multi-select-controls">
              <button 
                class="btn btn-sm" 
                :class="{ 'btn-primary': multiSelectMode, 'btn-outline-secondary': !multiSelectMode }" 
                @click="toggleMultiSelectMode"
              >
                <span class="material-icons">{{ multiSelectMode ? 'check_box' : 'check_box_outline_blank' }}</span>
                {{ multiSelectMode ? 'Terminer' : 'Sélection multiple' }}
              </button>
              
              <div v-if="multiSelectMode" class="select-all-controls">
                <button class="btn btn-sm btn-outline-primary" @click="selectAllAgents" title="Sélectionner tous les agents">
                  <span class="material-icons">select_all</span>
                </button>
                <button class="btn btn-sm btn-outline-secondary" @click="deselectAllAgents" title="Désélectionner tous les agents">
                  <span class="material-icons">clear_all</span>
                </button>
              </div>
            </div>
          </div>
          
          <div class="personnel-list" ref="personnelList">
            <div v-for="agent in filteredAgents" :key="agent.num_personnel" 
                class="agent-item" 
                :class="{ 'selected': isAgentSelected(agent) }" 
                draggable="true" 
                @dragstart="onDragStart($event, agent)" 
                @dragend="onDragEnd($event)" 
                @click="handleAgentClick(agent, $event)">
              <div class="agent-avatar" :style="{ backgroundColor: getAvatarColor(agent) }">
                {{ getInitials(agent) }}
              </div>
              <div class="agent-details">
                <div class="agent-name">{{ agent.nom }} {{ agent.prenom }}</div>
                <!--<div class="agent-info">{{ agent.grade }} - {{ getDirectionName(agent.direction_id) }}</div>-->
              </div>
              <div class="agent-actions" v-if="!multiSelectMode">
                <button class="action-btn" @click.stop="showAddAssignmentModal(agent)" title="Assigner à un site">
                  <span class="material-icons">assignment_ind</span>
                </button>
              </div>
              <div class="selection-indicator" v-if="multiSelectMode">
                <span class="material-icons">{{ isAgentSelected(agent) ? 'check_box' : 'check_box_outline_blank' }}</span>
              </div>
            </div>
            
            <div v-if="filteredAgents.length === 0" class="empty-list">
              <span class="material-icons">person_search</span>
              <p>Aucun agent trouvé</p>
            </div>
          </div>
        </div>

        <!-- Conteneur des sites -->
        <div class="sites-container">
          <div class="site-grid-wrapper" ref="siteGridWrapper">
            <div class="site-grid" ref="siteGrid">
              <div v-for="(site, index) in filteredSites" :key="site.id" class="site-card draggable" 
                :data-site-id="site.id" 
                :data-site-index="index"
                draggable="true"
                @dragstart="onSiteDragStart($event, site, index)"
                @dragend="onSiteDragEnd"
                @dragenter="onSiteDragEnter($event, index)"
                @dragleave="onSiteDragLeave"
                @dragover="onDragOver($event, site.id)"
                @drop="onDrop($event, site.id)">
                <div class="site-header" :style="{ backgroundColor: site.color || '#30C7B5' }">
                  <h3>
                    {{ site.name }}
                    <span class="assignment-count">{{ getAssignmentsForSite(site.id)?.length || 0 }}</span>
                  </h3>
                  <div class="site-details">
                    <span class="site-address">{{ site.address || 'Aucune adresse' }}</span>
                  </div>
                </div>

                <!-- Debut des assignations -->
                <div class="site-assignments" :style="{ '--site-color': site.color || '#30B8B2' }">
                  <!-- Recherche des assignations -->
                  <div class="site-search-box">
                    <input 
                      type="text" 
                      :placeholder="'Rechercher dans ' + site.name" 
                      v-model="siteSearchQueries[site.id]" 
                      @input="filterSiteAssignments(site.id)" 
                      class="site-search-input"
                    />
                    <button 
                      v-if="siteSearchQueries[site.id]" 
                      class="clear-site-search" 
                      @click="clearSiteSearch(site.id)" 
                      :style="{ color: site.color || '#30B8B2' }"
                    >
                      <span class="material-icons">close</span>
                    </button>
                  </div>
                  <!-- Fin recherche des assignations -->

                  <div v-for="assignment in getAssignmentsForSite(site.id) || []" :key="assignment.id" class="assignment-item" :data-assignment-id="assignment.id">
                    <div class="agent-avatar" :style="{ backgroundColor: getAvatarColor(assignment.agent) }">
                      {{ getInitials(assignment.agent) }}
                    </div>
                    <div class="assignment-details">
                      <div class="agent-name">
                        {{ assignment.agent?.nom || 'Agent inconnu' }} {{ assignment.agent?.prenom || '' }}
                      </div>
                      <div class="agent-details">
                        {{ assignment.agent?.grade || 'Grade inconnu' }} - {{ getDirectionName(assignment.agent?.direction_id) }}
                      </div>
                    </div>
                    <div class="assignment-actions">
                      <button class="action-btn delete-btn" @click="confirmDeleteAssignment(assignment)">
                        <span class="material-icons">delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de confirmation de suppression -->
    <dialog ref="confirmDeleteModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button class="btn-close" @click="closeConfirmModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir supprimer l'assignation de "{{ assignmentToDelete?.agent?.nom }} {{ assignmentToDelete?.agent?.prenom || '' }}" ?</p>
        <p class="warning">Cette action est irréversible.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmModal">Annuler</button>
        <button class="btn btn-danger" @click="deleteAssignment">Supprimer</button>
      </div>
    </dialog>
    
    <!-- Modal de confirmation d'assignation -->
    <dialog ref="confirmAssignModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer l'assignation</h3>
        <button class="btn-close" @click="closeAssignModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p v-if="selectedPersonnel.length === 1">Assigner "{{ selectedPersonnel[0]?.nom }} {{ selectedPersonnel[0]?.prenom || '' }}" au site "{{ siteToAssign?.name }}" ?</p>
        <p v-else>Assigner {{ selectedPersonnel.length }} agents au site "{{ siteToAssign?.name }}" ?</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeAssignModal">Annuler</button>
        <button class="btn btn-primary" @click="confirmAssign">Assigner</button>
      </div>
    </dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import Header from '@/components/Header.vue';
import api from '@/services/api';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import '@/assets/css/site-assignment-styles.css';

const router = useRouter();

// États réactifs
const loading = ref(true);
const sites = ref([]);
const personnel = ref([]);
const assignments = ref([]);
const savingInProgress = ref(false);
const showAssignmentModal = ref(false);
const showDeleteModal = ref(false);
const showExportMenu = ref(false);
const isEditMode = ref(false);
const assignmentToDelete = ref(null);
const toasts = ref([]);
const directions = ref([]);

// États pour le drag and drop
const selectedPersonnel = ref([]);
const siteToAssign = ref(null);
const multiSelectMode = ref(false);
const personnelSearchQuery = ref('');

// Variables pour le drag and drop des sites
const draggedSite = ref(null);
const draggedSiteIndex = ref(-1);
const dragOverSiteIndex = ref(-1);

// États pour les modales de confirmation
const confirmDeleteModal = ref(null);
const confirmAssignModal = ref(null);

// États pour le toast
const toastType = ref('');
const toastIcon = ref('');
const toastTitle = ref('');
const toastMessage = ref('');
const toastProgress = ref(0);

// Référence pour la grille des sites (défilement horizontal)
const siteGrid = ref(null);

// Filtres
const filterDirection = ref('');
const filterSite = ref('');
const filterType = ref('');
const searchQuery = ref('');
const siteSearchQueries = reactive({});
const filteredAssignmentsBySite = reactive({});

// Nouvelle assignation
const newAssignment = reactive({
  id: null,
  num_personnel: '',
  site_id: ''
});

// Computed properties pour le filtrage
const filteredAgents = computed(() => {
  if (!personnel.value || !Array.isArray(personnel.value)) {
    return [];
  }
  
  return personnel.value.filter(agent => {
    if (!agent) return false;
    
    // Filtre par direction
    if (filterDirection.value && agent.direction_id !== filterDirection.value) {
      return false;
    }
    
    // Filtre par recherche
    const query = personnelSearchQuery.value.toLowerCase().trim();
    if (query) {
      return agent.nom.toLowerCase().includes(query) || 
             (agent.prenom && agent.prenom.toLowerCase().includes(query)) || 
             (agent.grade && agent.grade.toLowerCase().includes(query)) || 
             (agent.num_personnel && agent.num_personnel.toLowerCase().includes(query));
    }
    
    return true;
  });
});

// Filtrer les sites en fonction de la recherche
const filteredSites = computed(() => {
  if (!filterSite.value) return sites.value;
  return sites.value.filter(site => site.id === filterSite.value);
});

const isFormValid = computed(() => {
  if (!newAssignment.num_personnel || !newAssignment.site_id) {
    return false;
  }
  
  return true;
});

onMounted(async () => {
  try {
    // Initialiser les tableaux vides pour éviter les erreurs
    sites.value = [];
    personnel.value = [];
    assignments.value = [];
    directions.value = [];
    
    // Les références aux modales sont automatiquement liées par Vue
    // grâce à la directive ref dans le template
    
    // Initialiser les objets réactifs pour les recherches de sites
    sites.value.forEach(site => {
      if (site && site.id) {
        siteSearchQueries[site.id] = '';
        filteredAssignmentsBySite[site.id] = [];
      }
    });
    
    // Chargement parallèle des données
    const [sitesResponse, personnelResponse, assignmentsResponse, directionsResponse] = await Promise.all([
      api.get('/api/sites'),
      api.get('/api/personnel'),
      api.get('/api/sites/personnel-sites/active'),
      api.get('/api/parametres/directions')
    ]);
    
    // Vérifier que les réponses contiennent des données valides
    if (sitesResponse?.data?.data && Array.isArray(sitesResponse.data.data)) {
      // Préparer les données des sites avec des couleurs
      const sitesData = sitesResponse.data.data.map(site => ({
        ...site,
        color: getRandomColor(site.id)
      }));
      
      // Récupérer l'ordre des sites depuis le localStorage s'il existe
      const savedOrder = localStorage.getItem('sitesOrder');
      if (savedOrder) {
        try {
          const orderMap = JSON.parse(savedOrder);
          // Trier les sites selon l'ordre sauvegardé
          sitesData.sort((a, b) => {
            const orderA = orderMap[a.id] !== undefined ? orderMap[a.id] : 999;
            const orderB = orderMap[b.id] !== undefined ? orderMap[b.id] : 999;
            return orderA - orderB;
          });
        } catch (e) {
          console.error('Erreur lors du parsing de l\'ordre des sites:', e);
        }
      }
      
      sites.value = sitesData;
      
      // Initialiser les recherches pour chaque site
      sites.value.forEach(site => {
        if (site && site.id) {
          siteSearchQueries[site.id] = '';
          filteredAssignmentsBySite[site.id] = [];
        }
      });
    }
    
    if (personnelResponse?.data?.data && Array.isArray(personnelResponse.data.data)) {
      personnel.value = personnelResponse.data.data;
    }
    
    if (directionsResponse?.data?.data && Array.isArray(directionsResponse.data.data)) {
      directions.value = directionsResponse.data.data;
    }

    if (assignmentsResponse?.data?.data && Array.isArray(assignmentsResponse.data.data)) {
      assignments.value = assignmentsResponse.data.data.map(assignment => {
        // Ajouter l'objet agent complet à chaque assignation
        const agent = personnel.value.find(p => p && p.num_personnel === assignment.num_personnel);
        return { ...assignment, agent: agent || {} };
      });
    }
    
    console.log('Sites chargés:', sites.value.length);
    console.log('Personnel chargé:', personnel.value.length);
    console.log('Assignations chargées:', assignments.value.length);
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error);
    showToast('error', 'Erreur', 'Impossible de charger les données. Veuillez rafraîchir la page.');
  } finally {
    loading.value = false;
  }
});

// Fonction pour générer une couleur aléatoire mais cohérente pour chaque site
const getRandomColor = (id) => {
  // Palette de couleurs turquoise OpSolver
  const colors = [
    '#30C7B5', // Turquoise/Vert menthe (principal)
    '#5DD6C7', // Turquoise clair
    '#26A194', // Turquoise foncé
    '#1D7A70', // Turquoise très foncé
    '#2B8FA6', // Bleu marine
    '#27B577'  // Vert émeraude
  ];
  
  // Utiliser l'ID pour sélectionner une couleur de manière cohérente
  const colorIndex = typeof id === 'number' ? id % colors.length : String(id).charCodeAt(0) % colors.length;
  return colors[colorIndex];
};

// Fonctions de drag and drop pour les agents
const onDragStart = (event, agent) => {
  // Si l'agent est déjà sélectionné, on va déplacer tous les agents sélectionnés
  if (isAgentSelected(agent)) {
    // Ne rien faire, car on utilise déjà selectedPersonnel.value
  } else {
    // Sinon, on sélectionne uniquement cet agent
    selectedPersonnel.value = [agent];
  }
  
  // Ajouter la classe dragging à l'élément en cours de déplacement
  event.target.classList.add('dragging');
  
  // Stocker l'ID de l'agent dans les données de transfert
  event.dataTransfer.setData('text/plain', JSON.stringify({
    type: 'agent',
    agents: selectedPersonnel.value.map(p => p.num_personnel)
  }));
  event.dataTransfer.effectAllowed = 'move';
};

const onDragEnd = (event) => {
  // Supprimer la classe dragging de l'élément
  event.target.classList.remove('dragging');
  
  // Supprimer la classe drop-target de tous les sites et réinitialiser les transformations
  document.querySelectorAll('.site-card').forEach(site => {
    site.classList.remove('drop-target');
    site.style.transform = '';
    site.classList.remove('drop-success');
  });
  
  // Réinitialiser les styles sur tous les agents
  document.querySelectorAll('.agent-item').forEach(agent => {
    agent.classList.remove('dragging');
  });
  
  // Log pour débogage
  console.log('Drag and drop terminé, effets visuels nettoyés');
};

const onDragOver = (event, siteId) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
  
  // Vérifier si les données transférées sont de type agent
  const data = event.dataTransfer.types.includes('text/plain') ? true : false;
  if (!data) return false;
  
  try {
    // Supprimer la classe drop-target de tous les sites
    document.querySelectorAll('.site-card').forEach(site => {
      site.classList.remove('drop-target');
    });
    
    // Ajouter la classe drop-target au site actuel
    const currentSiteElement = event.currentTarget;
    if (currentSiteElement && currentSiteElement.classList) {
      currentSiteElement.classList.add('drop-target');
      
      // Ajouter un effet de pulsation plus prononcé
      currentSiteElement.style.transform = 'translateY(-5px)';
      
      // Réinitialiser l'effet après un court délai pour éviter les accumulations
      setTimeout(() => {
        if (currentSiteElement.classList.contains('drop-target')) {
          currentSiteElement.style.transform = 'translateY(-5px)';
        } else {
          currentSiteElement.style.transform = '';
        }
      }, 100);
    }
  } catch (error) {
    console.error('Erreur lors du dragOver:', error);
  }
  
  return false;
};

// Fonctions de drag and drop pour les sites
const onSiteDragStart = (event, site, index) => {
  console.log('Site drag start', site, index);
  draggedSite.value = site;
  draggedSiteIndex.value = index;
  
  // Ajouter une classe pour indiquer que le site est en train d'être déplacé
  event.target.classList.add('dragging');
  
  // Stocker l'ID du site et son index dans les données de transfert
  event.dataTransfer.setData('text/plain', JSON.stringify({
    type: 'site',
    id: site.id,
    index
  }));
  event.dataTransfer.effectAllowed = 'move';
  
  // Créer une image personnalisée pour le drag
  const dragImage = document.createElement('div');
  dragImage.classList.add('site-drag-image');
  dragImage.textContent = site.name;
  dragImage.style.backgroundColor = site.color || '#30C7B5';
  dragImage.style.color = 'white';
  dragImage.style.padding = '10px';
  dragImage.style.borderRadius = '6px';
  dragImage.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
  dragImage.style.position = 'absolute';
  dragImage.style.top = '-1000px';
  dragImage.style.opacity = '0.9';
  
  document.body.appendChild(dragImage);
  event.dataTransfer.setDragImage(dragImage, 20, 20);
  
  // Supprimer l'élément après un court délai
  setTimeout(() => {
    document.body.removeChild(dragImage);
  }, 100);
};

const onSiteDragEnd = (event) => {
  console.log('Site drag end');
  
  // Supprimer la classe de style
  event.target.classList.remove('dragging');
  
  // Supprimer toutes les classes de survol
  const siteCards = document.querySelectorAll('.site-card');
  siteCards.forEach(card => {
    card.classList.remove('drag-over');
  });
  
  // Réinitialiser les variables
  draggedSite.value = null;
  draggedSiteIndex.value = -1;
  dragOverSiteIndex.value = -1;
};

const onSiteDragEnter = (event, index) => {
  console.log('Site drag enter', index);
  
  // Si on est en train de faire glisser un site et qu'on entre dans un autre site
  if (draggedSite.value && draggedSiteIndex.value !== index) {
    // Ajouter une classe pour indiquer la zone de drop
    event.currentTarget.classList.add('drag-over');
    dragOverSiteIndex.value = index;
  }
};

const onSiteDragLeave = (event) => {
  console.log('Site drag leave');
  
  // Supprimer la classe de survol
  event.currentTarget.classList.remove('drag-over');
};

const onSiteDrop = (event, index) => {
  console.log('Site drop', index);
  
  // Empêcher le comportement par défaut
  event.preventDefault();
  
  // Supprimer la classe de survol
  event.currentTarget.classList.remove('drag-over');
  
  // Si on est en train de faire glisser un site et qu'on le dépose sur un autre site
  if (draggedSite.value && draggedSiteIndex.value !== index) {
    // Réorganiser les sites
    const newSites = [...sites.value];
    const [movedSite] = newSites.splice(draggedSiteIndex.value, 1);
    newSites.splice(index, 0, movedSite);
    
    // Mettre à jour l'ordre des sites
    sites.value = newSites;
    
    // Mettre à jour l'ordre dans la base de données
    saveSitesOrder(newSites);
    
    // Afficher une notification de succès
    showStyledToast('success', 'Ordre mis à jour', 'L\'ordre des sites a été modifié avec succès');
  }
  
  // Réinitialiser les variables
  draggedSite.value = null;
  draggedSiteIndex.value = -1;
  dragOverSiteIndex.value = -1;
};

const onDrop = (event, siteId) => {
  event.preventDefault();
  event.stopPropagation();
  
  // Supprimer la classe drop-target de tous les sites
  document.querySelectorAll('.site-card').forEach(site => {
    site.classList.remove('drop-target');
    site.style.transform = '';
  });
  
  const data = event.dataTransfer.getData('text/plain');
  if (!data) return;
  
  try {
    const transferData = JSON.parse(data);
    if (transferData.type === 'site') return;
    
    if (transferData.type === 'agent' && Array.isArray(transferData.agents)) {
      // Ajouter une classe temporaire pour l'animation
      const targetElement = event.currentTarget;
      if (targetElement && targetElement.classList) {
        // Ajouter l'effet visuel de succès
        targetElement.classList.add('drop-success');
        
        // Ajouter un léger effet de rebond
        targetElement.style.transform = 'translateY(-8px)';
        setTimeout(() => {
          targetElement.style.transform = 'translateY(-3px)';
          setTimeout(() => {
            targetElement.style.transform = '';
          }, 300);
        }, 150);
        
        // Retirer la classe drop-success après l'animation
        setTimeout(() => {
          targetElement.classList.remove('drop-success');
        }, 800);
      }
      
      const targetSite = sites.value.find(site => site.id === siteId);
      if (!targetSite) {
        showStyledToast('error', 'Erreur', 'Site non trouvé. Impossible de procéder à l\'assignation.');
        return;
      }
      
      // Préparer les données pour la modale de confirmation
      siteToAssign.value = targetSite;
      selectedPersonnel.value = [];
      
      transferData.agents.forEach(numPersonnel => {
        const agent = personnel.value.find(p => p.num_personnel === numPersonnel);
        if (agent) {
          selectedPersonnel.value.push(agent);
        }
      });
      
      if (selectedPersonnel.value.length === 0) {
        showStyledToast('error', 'Erreur', 'Aucun agent valide à assigner.');
        return;
      }
      
      // Ouvrir la modale de confirmation
      if (confirmAssignModal.value) {
        confirmAssignModal.value.showModal();
      } else {
        console.error('La référence à la modale de confirmation est null ou undefined');
        showStyledToast('error', 'Erreur', 'Impossible d\'ouvrir la boîte de dialogue de confirmation.');
      }
    }
  } catch (error) {
    console.error('Erreur lors du drop:', error);
    showStyledToast('error', 'Erreur', 'Une erreur est survenue lors du déplacement.');
  }
};

// Fonction pour sauvegarder l'ordre des sites
const saveSitesOrder = (sitesList) => {
  try {
    // Créer un objet qui associe chaque ID de site à sa position
    const orderMap = {};
    sitesList.forEach((site, index) => {
      orderMap[site.id] = index;
    });
    
    // Sauvegarder dans le localStorage
    localStorage.setItem('sitesOrder', JSON.stringify(orderMap));
    
    // Optionnel: Sauvegarder l'ordre dans la base de données via l'API
    // Cette partie dépend de votre API backend
    // api.post('/api/sites/order', { order: orderMap });
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de l\'ordre des sites:', error);
    showStyledToast('error', 'Erreur', 'Impossible de sauvegarder l\'ordre des sites');
  }
};

// Fonctions de gestion de la sélection multiple
function isAgentSelected(agent) {
  if (!agent || !selectedPersonnel.value) return false;
  return selectedPersonnel.value.some(p => p.num_personnel === agent.num_personnel);
}

function toggleAgentSelection(agent, event) {
  if (!agent) return;
  
  // En mode sélection multiple, on ajoute ou retire simplement l'agent de la sélection
  if (multiSelectMode.value) {
    if (isAgentSelected(agent)) {
      selectedPersonnel.value = selectedPersonnel.value.filter(p => p.num_personnel !== agent.num_personnel);
    } else {
      selectedPersonnel.value.push(agent);
    }
  } else {
    // Comportement normal (avec Ctrl pour sélection multiple)
    if (event && event.ctrlKey) {
      if (isAgentSelected(agent)) {
        selectedPersonnel.value = selectedPersonnel.value.filter(p => p.num_personnel !== agent.num_personnel);
      } else {
        selectedPersonnel.value.push(agent);
      }
    } else {
      if (isAgentSelected(agent) && selectedPersonnel.value.length === 1) {
        selectedPersonnel.value = [];
      } else {
        selectedPersonnel.value = [agent];
      }
    }
  }
}

// Sélectionner tous les agents affichés dans la liste filtrée
function selectAllAgents() {
  // Utiliser les agents filtrés pour ne sélectionner que ceux qui sont visibles
  const agentsToSelect = filteredAgents.value || [];
  
  // Remplacer la sélection actuelle par tous les agents filtrés
  selectedPersonnel.value = [...agentsToSelect];
  
  // Afficher un toast de confirmation
  if (agentsToSelect.length > 0) {
    showToast('success', 'Sélection', `${agentsToSelect.length} agent(s) sélectionné(s).`);
  } else {
    showToast('info', 'Information', 'Aucun agent disponible à sélectionner.');
  }
}

// Désélectionner tous les agents
function deselectAllAgents() {
  const previousCount = selectedPersonnel.value.length;
  selectedPersonnel.value = [];
  
  // Afficher un toast de confirmation si des agents étaient sélectionnés
  if (previousCount > 0) {
    showToast('info', 'Sélection', 'Sélection effacée.');
  }
}

// Variable pour stocker le dernier agent sélectionné (pour la sélection avec Shift)
const lastSelectedAgent = ref(null);

// Référence pour le bouton d'export
const exportButton = ref(null);

// Position du menu déroulant
const dropdownPosition = ref({
  top: '0px',
  left: '0px'
});

// Nouvelle méthode pour gérer les clics sur les agents
function handleAgentClick(agent, event) {
  if (multiSelectMode.value) {
    // En mode sélection multiple, on bascule simplement l'état de sélection
    toggleAgentSelection(agent, event);
    lastSelectedAgent.value = agent;
  } else if (event.ctrlKey) {
    // Si Ctrl est pressé, on active temporairement le mode multi-sélection
    toggleAgentSelection(agent, event);
    lastSelectedAgent.value = agent;
  } else if (event.shiftKey && lastSelectedAgent.value) {
    // Si Shift est pressé et qu'un agent a déjà été sélectionné
    // Sélectionner tous les agents entre le dernier sélectionné et celui-ci
    selectAgentRange(lastSelectedAgent.value, agent);
  } else {
    // Sélection simple d'un agent
    selectedPersonnel.value = [agent];
    lastSelectedAgent.value = agent;
  }
}

// Fonction pour sélectionner une plage d'agents (pour la sélection avec Shift)
function selectAgentRange(startAgent, endAgent) {
  if (!startAgent || !endAgent || !filteredAgents.value) return;
  
  // Trouver les indices des agents de début et de fin dans la liste filtrée
  const startIndex = filteredAgents.value.findIndex(a => a.num_personnel === startAgent.num_personnel);
  const endIndex = filteredAgents.value.findIndex(a => a.num_personnel === endAgent.num_personnel);
  
  if (startIndex === -1 || endIndex === -1) return;
  
  // Déterminer l'ordre de sélection (ascendant ou descendant)
  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  
  // Sélectionner tous les agents dans la plage
  const newSelection = [];
  for (let i = minIndex; i <= maxIndex; i++) {
    newSelection.push(filteredAgents.value[i]);
  }
  
  // Mettre à jour la sélection
  selectedPersonnel.value = newSelection;
  lastSelectedAgent.value = endAgent;
}

function toggleMultiSelectMode() {
  multiSelectMode.value = !multiSelectMode.value;
  if (!multiSelectMode.value) {
    selectedPersonnel.value = [];
  }
}

const togglePersonnelSelection = (agent) => {
  const index = selectedPersonnel.value.findIndex(p => p.num_personnel === agent.num_personnel);
  if (index === -1) {
    selectedPersonnel.value.push(agent);
  } else {
    selectedPersonnel.value.splice(index, 1);
  }
};

const getDirectionName = (directionId) => {
  // Si l'ID de direction est null, undefined ou invalide
  if (directionId === null || directionId === undefined) {
    return 'Direction inconnue';
  }
  
  // Chercher la direction dans la liste
  const direction = directions.value.find(d => d.id === directionId);
  return direction ? direction.nom : 'Direction inconnue';
};

// Fonction pour générer une couleur d'avatar cohérente pour chaque agent
const getAvatarColor = (agent) => {
  if (!agent) return '#30C7B5'; // Couleur par défaut (turquoise OpSolver)
  
  // Utiliser le numéro de personnel ou l'ID comme graine pour la couleur
  const seed = agent.num_personnel || agent.id || '';
  
  // Palette de couleurs OpSolver
  const colors = [
    '#30C7B5', // Turquoise principal
    '#5DD6C7', // Turquoise clair
    '#26A194', // Turquoise foncé
    '#1D7A70', // Turquoise très foncé
    '#FF7A68', // Corail doux
    '#2B8FA6', // Bleu marine
    '#27B577'  // Vert émeraude
  ];
  
  // Générer un index basé sur la graine
  let hash = 0;
  for (let i = 0; i < seed.toString().length; i++) {
    hash = seed.toString().charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Utiliser le hash pour sélectionner une couleur dans la palette
  const colorIndex = Math.abs(hash) % colors.length;
  return colors[colorIndex];
};

// Fonction pour obtenir les initiales d'un agent
const getInitials = (agent) => {
  if (!agent) return '';
  
  const nom = agent.nom || '';
  const prenom = agent.prenom || '';
  
  let initials = '';
  if (nom) initials += nom.charAt(0).toUpperCase();
  if (prenom) initials += prenom.charAt(0).toUpperCase();
  
  return initials || '?';
};

// Fonction pour obtenir les assignations filtrées pour un site spécifique
const getAssignmentsForSite = (siteId) => {
  if (!siteId || !assignments.value) return [];
  
  // Si des assignations filtrées existent pour ce site, les utiliser
  if (filteredAssignmentsBySite[siteId] && filteredAssignmentsBySite[siteId].length > 0) {
    return filteredAssignmentsBySite[siteId];
  }
  
  // Sinon, filtrer toutes les assignations pour ce site
  const siteAssignments = assignments.value.filter(assignment => {
    return assignment.site_id === siteId;
  });
  
  // Vérifier et corriger les assignations qui n'auraient pas d'agent valide
  return siteAssignments.map(assignment => {
    // Si l'agent est déjà valide, retourner l'assignation telle quelle
    if (assignment.agent && assignment.agent.nom) {
      return assignment;
    }
    
    // Sinon, essayer de trouver l'agent dans la liste du personnel
    const agent = personnel.value.find(p => p && p.num_personnel === assignment.num_personnel);
    
    // Retourner l'assignation avec un agent valide ou un objet par défaut
    return {
      ...assignment,
      agent: agent || {
        nom: 'Agent inconnu',
        prenom: '',
        grade: 'Grade inconnu',
        direction_id: null,
        num_personnel: assignment.num_personnel
      }
    };
  });
};

// Fonction pour filtrer les assignations d'un site selon la recherche
const filterSiteAssignments = (siteId) => {
  if (!siteId || !assignments.value) return;
  
  const query = siteSearchQueries[siteId]?.toLowerCase().trim() || '';
  
  // Si la requête est vide, réinitialiser le filtre
  if (!query) {
    filteredAssignmentsBySite[siteId] = [];
    return;
  }
  
  // Filtrer les assignations pour ce site selon la requête
  // et s'assurer que chaque assignation a un agent valide
  filteredAssignmentsBySite[siteId] = assignments.value
    .filter(assignment => assignment.site_id === siteId)
    .map(assignment => {
      // Si l'agent est déjà valide, retourner l'assignation telle quelle
      if (assignment.agent && assignment.agent.nom) {
        return assignment;
      }
      
      // Sinon, essayer de trouver l'agent dans la liste du personnel
      const agent = personnel.value.find(p => p && p.num_personnel === assignment.num_personnel);
      
      // Retourner l'assignation avec un agent valide ou un objet par défaut
      return {
        ...assignment,
        agent: agent || {
          nom: 'Agent inconnu',
          prenom: '',
          grade: 'Grade inconnu',
          direction_id: null,
          num_personnel: assignment.num_personnel
        }
      };
    })
    .filter(assignment => {
      const agent = assignment.agent;
      // Maintenant que nous avons un agent valide, filtrer selon la requête
      return (
        agent.nom?.toLowerCase().includes(query) ||
        agent.prenom?.toLowerCase().includes(query) ||
        agent.grade?.toLowerCase().includes(query) ||
        agent.num_personnel?.toLowerCase().includes(query)
      );
    });
};

// Fonction pour effacer la recherche dans un site
const clearSiteSearch = (siteId) => {
  if (siteId && siteSearchQueries[siteId] !== undefined) {
    siteSearchQueries[siteId] = '';
    filteredAssignmentsBySite[siteId] = [];
  }
};

// Fonctions pour fermer les modales
const closeAssignModal = () => {
  if (confirmAssignModal.value) {
    confirmAssignModal.value.close();
  }
  siteToAssign.value = null;
};

const closeModal = () => {
  // Note: formModal n'est pas utilisé dans cette vue, cette fonction est conservée
  // pour compatibilité avec d'autres parties du code
  newAssignment.value = { agent_id: null, site_id: null };
  isEditMode.value = false;
};

const closeConfirmModal = () => {
  if (confirmDeleteModal.value) {
    confirmDeleteModal.value.close();
  }
  assignmentToDelete.value = null;
};

// Fonction pour confirmer la suppression d'une assignation
const confirmDeleteAssignment = (assignment) => {
  if (!assignment) return;
  
  // Stocker l'assignation à supprimer
  assignmentToDelete.value = assignment;
  
  // Ouvrir la modal de confirmation
  if (confirmDeleteModal.value) {
    confirmDeleteModal.value.showModal();
  }
};

// La fonction showStyledToast est déjà déclarée ailleurs dans le code

const confirmAssign = async () => {
  try {
    savingInProgress.value = true;
    
    // Vérifier que le site est bien défini
    if (!siteToAssign.value || !siteToAssign.value.id) {
      showStyledToast('error', 'Erreur', 'Aucun site sélectionné pour l\'assignation.');
      return;
    }
    
    if (selectedPersonnel.value.length === 0) {
      showStyledToast('error', 'Erreur', 'Aucun agent sélectionné pour l\'assignation.');
      return;
    }
    
    console.log('Assignation en cours pour:', {
      site: siteToAssign.value,
      agents: selectedPersonnel.value
    });
    
    // Créer les assignations pour chaque agent sélectionné
    let successCount = 0;
    let errorCount = 0;
    
    for (const agent of selectedPersonnel.value) {
      try {
        // Créer l'assignation pour cet agent
        await api.post('/api/sites/assign', {
          num_personnel: agent.num_personnel,
          site_id: siteToAssign.value.id
        });
        successCount++;
      } catch (err) {
        console.error(`Erreur lors de l'assignation de l'agent ${agent.num_personnel}:`, err);
        errorCount++;
      }
    }
    
    // Recharger les assignations pour mettre à jour l'interface
    try {
      const assignmentsResponse = await api.get('/api/sites/personnel-sites/active');
      if (assignmentsResponse?.data?.data) {
        // Stocker les nouveaux IDs pour pouvoir les marquer avec la classe new-assignment
        const newAssignmentIds = [];
        
        // Identifier les nouvelles assignations en comparant avec les assignations existantes
        if (assignments.value && Array.isArray(assignments.value)) {
          const existingIds = assignments.value.map(a => a.id);
          newAssignmentIds.push(...assignmentsResponse.data.data
            .filter(a => !existingIds.includes(a.id))
            .map(a => a.id));
        }
        
        // Mettre à jour les assignations en s'assurant que chaque assignation a un objet agent valide
        assignments.value = assignmentsResponse.data.data.map(assignment => {
          // Trouver l'agent correspondant dans la liste du personnel
          const agent = personnel.value.find(p => p && p.num_personnel === assignment.num_personnel);
          
          // S'assurer que l'objet agent est complet et valide
          return { 
            ...assignment, 
            agent: agent || { 
              nom: 'Agent inconnu', 
              prenom: '', 
              grade: 'Grade inconnu',
              direction_id: null,
              num_personnel: assignment.num_personnel
            } 
          };
        });
        
        // Ajouter la classe new-assignment aux nouvelles assignations après un court délai
        // pour permettre au DOM de se mettre à jour
        setTimeout(() => {
          newAssignmentIds.forEach(id => {
            const elements = document.querySelectorAll(`.assignment-item[data-assignment-id="${id}"]`);
            elements.forEach(el => {
              el.classList.add('new-assignment');
              // Retirer la classe après l'animation
              setTimeout(() => {
                el.classList.remove('new-assignment');
              }, 2000);
            });
          });
        }, 100);
      }
    } catch (err) {
      console.error('Erreur lors du rechargement des assignations:', err);
    }
    
    // Afficher un message de réussite ou d'échec
    if (successCount > 0) {
      showStyledToast(
        errorCount > 0 ? 'warning' : 'success',
        errorCount > 0 ? 'Assignation partielle' : 'Assignation réussie',
        `${successCount} agent(s) assigné(s) avec succès${errorCount > 0 ? ` (${errorCount} échecs)` : ''} au site ${siteToAssign.value.name}.`
      );
    } else {
      showStyledToast('error', 'Erreur', 'Impossible de créer les assignations. Veuillez réessayer.');
    }
    
    // Réinitialiser la sélection
    deselectAllAgents();
    
    // Fermer la modale si elle est ouverte
    if (confirmAssignModal.value) {
      confirmAssignModal.value.close();
    }
    
    // Réinitialiser le site à assigner
    siteToAssign.value = null;
  } catch (error) {
    console.error('Erreur globale lors de l\'assignation:', error);
    showStyledToast('error', 'Erreur', 'Une erreur inattendue est survenue. Veuillez réessayer.');
  } finally {
    savingInProgress.value = false;
  }
};

// Fonctions de sauvegarde et suppression
async function saveAssignment() {
  try {
    const assignmentData = { ...newAssignment };
    
    // Ajouter ou mettre à jour l'assignation
    let response;
    if (isEditMode.value) {
      response = await api.put(`/site-assignments/${assignmentData.id}`, assignmentData);
      // Mettre à jour l'assignation dans le tableau
      const index = assignments.value.findIndex(a => a.id === assignmentData.id);
      if (index !== -1) {
        const agent = personnel.value.find(p => p.id === assignmentData.agent_id);
        assignments.value[index] = { ...response.data, agent };
      }
      showToast('success', 'Succès', 'Assignation mise à jour avec succès.');
    } else {
      response = await api.post('/site-assignments', assignmentData);
      // Ajouter la nouvelle assignation au tableau
      const agent = personnel.value.find(p => p.id === assignmentData.agent_id);
      assignments.value.push({ ...response.data, agent });
      showToast('success', 'Succès', 'Nouvelle assignation créée avec succès.');
    }
    
    closeModal();
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de l\'assignation:', error);
    showToast('error', 'Erreur', 'Impossible de sauvegarder l\'assignation. Veuillez réessayer.');
  }
}

const deleteAssignment = async () => {
  try {
    savingInProgress.value = true;
    
    if (!assignmentToDelete.value) {
      showStyledToast('error', 'Erreur', 'Impossible de supprimer cette assignation: informations manquantes.');
      closeConfirmModal();
      return;
    }
    
    // Vérifier quel type d'assignation nous avons (avec id ou avec num_personnel/site_id)
    if (assignmentToDelete.value.id) {
      // Cas 1: Assignation avec un ID (depuis la vue des sites)
      await api.delete(`/api/sites/personnel-sites/${assignmentToDelete.value.id}`);
      
      // Mettre à jour la liste des assignations localement en filtrant par id
      assignments.value = assignments.value.filter(a => a.id !== assignmentToDelete.value.id);
      
      showStyledToast('success', 'Suppression réussie', 'L\'assignation a été supprimée avec succès.');
    } 
    else if (assignmentToDelete.value.num_personnel && assignmentToDelete.value.site_id) {
      // Cas 2: Assignation avec num_personnel et site_id (depuis la liste du personnel)
      await api.delete(`/api/sites/unassign/${assignmentToDelete.value.num_personnel}/${assignmentToDelete.value.site_id}`);
      
      // Mettre à jour la liste des assignations localement
      assignments.value = assignments.value.filter(a => 
        !(a.num_personnel === assignmentToDelete.value.num_personnel && 
          a.site_id === assignmentToDelete.value.site_id)
      );
      
      showStyledToast('success', 'Succès', 'Assignation supprimée avec succès.');
    } else {
      throw new Error('Format d\'assignation non reconnu');
    }
    
    closeConfirmModal();
    assignmentToDelete.value = null;
  } catch (error) {
    console.error('Erreur lors de la suppression:', error);
    showToast('error', 'Erreur', 'Impossible de supprimer l\'assignation. Veuillez réessayer.');
  } finally {
    savingInProgress.value = false;
  }
};

async function saveAllAssignments() {
  savingInProgress.value = true;
  
  try {
    await api.post('/site-assignments/batch', { assignments: assignments.value });
    showToast('success', 'Succès', 'Toutes les assignations ont été enregistrées avec succès.');
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement des assignations:', error);
    showToast('error', 'Erreur', 'Impossible d\'enregistrer les assignations. Veuillez réessayer.');
  } finally {
    savingInProgress.value = false;
  }
}

// Fonction pour fermer le menu d'export quand on clique ailleurs
function closeExportMenuOnClickOutside(event) {
  // Si le menu est ouvert et que le clic n'est pas sur le bouton d'export ou sur le menu lui-même
  if (showExportMenu.value && exportButton.value && !exportButton.value.contains(event.target) && 
      !event.target.closest('.dropdown-menu-portal')) {
    showExportMenu.value = false;
    window.removeEventListener('click', closeExportMenuOnClickOutside);
  }
}

// Fonctions d'exportation
function toggleExportMenu() {
  if (!showExportMenu.value) {
    // Calculer la position du menu déroulant par rapport au bouton
    if (exportButton.value) {
      const rect = exportButton.value.getBoundingClientRect();
      dropdownPosition.value = {
        top: `${rect.bottom + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        minWidth: `${rect.width}px`
      };
    }
  }
  showExportMenu.value = !showExportMenu.value;
  
  // Ajouter un écouteur d'événements pour fermer le menu si on clique ailleurs
  if (showExportMenu.value) {
    setTimeout(() => {
      window.addEventListener('click', closeExportMenuOnClickOutside);
    }, 0);
  }
}

function exportToExcel() {
  // Fermer le menu déroulant
  showExportMenu.value = false;
  window.removeEventListener('click', closeExportMenuOnClickOutside);
  
  // Préparer les données pour l'export, groupées par site
  const assignmentsBySite = {};
  
  // Grouper les assignations par site
  assignments.value.forEach(assignment => {
    if (!assignment.site_id) return;
    
    if (!assignmentsBySite[assignment.site_id]) {
      assignmentsBySite[assignment.site_id] = [];
    }
    
    assignmentsBySite[assignment.site_id].push(assignment);
  });
  
  // Trier les sites par nom
  const sortedSiteIds = Object.keys(assignmentsBySite).sort((a, b) => {
    const siteA = sites.value.find(s => s.id === parseInt(a));
    const siteB = sites.value.find(s => s.id === parseInt(b));
    return (siteA?.name || '').localeCompare(siteB?.name || '');
  });
  
  // Créer un nouveau workbook
  const workbook = XLSX.utils.book_new();
  
  // Pour chaque site, créer une feuille Excel séparée
  sortedSiteIds.forEach((siteId) => {
    const siteAssignments = assignmentsBySite[siteId];
    const site = sites.value.find(s => s.id === parseInt(siteId));
    const siteName = site?.name || 'Non défini';
    
    // Préparer les données pour la feuille du site actuel
    const sheetData = siteAssignments.map((assignment, index) => {
      // Récupérer le nom de la direction
      const directionName = assignment.agent?.direction_id ? 
        getDirectionName(assignment.agent.direction_id) : 'Non définie';
        
      return {
        'N°': index + 1, // Numéro d'ordre
        'Noms': `${assignment.agent?.nom || ''} ${assignment.agent?.prenom || ''}`,
        'Sexe': assignment.agent?.sexe || '',
        'Grade': assignment.agent?.grade || '',
        'Direction': directionName,
        'Site': siteName
      };
    });
    
    // Créer la feuille pour ce site
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    
    // Ajouter des styles (largeur des colonnes)
    const columnWidths = [
      { wch: 5 },  // N°
      { wch: 30 }, // Noms
      { wch: 10 }, // Sexe
      { wch: 20 }, // Grade
      { wch: 25 }, // Direction
      { wch: 25 }  // Site
    ];
    
    worksheet['!cols'] = columnWidths;
    
    // Limiter le nom de la feuille à 31 caractères (limite Excel)
    let sheetName = siteName;
    if (sheetName.length > 28) {
      sheetName = sheetName.substring(0, 28) + '...';
    }
    
    // Ajouter la feuille au workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });
  
  // Générer le fichier Excel
  const date = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
  XLSX.writeFile(workbook, `assignations_sites_${date}.xlsx`);
  
  showToast('success', 'Export réussi', 'Les données ont été exportées au format Excel.');
}

async function exportToPDF() {
  // Importer dynamiquement jspdf-autotable
  const autoTable = (await import('jspdf-autotable')).default;
  
  // Créer le document PDF
  const doc = new jsPDF();
  
  // Fermer le menu déroulant
  showExportMenu.value = false;
  window.removeEventListener('click', closeExportMenuOnClickOutside);
  
  // Préparer les données pour le tableau, groupées par site
  const assignmentsBySite = {};
  
  // Grouper les assignations par site
  assignments.value.forEach(assignment => {
    if (!assignment.site_id) return;
    
    if (!assignmentsBySite[assignment.site_id]) {
      assignmentsBySite[assignment.site_id] = [];
    }
    
    assignmentsBySite[assignment.site_id].push(assignment);
  });
  
  // Trier les sites par nom
  const sortedSiteIds = Object.keys(assignmentsBySite).sort((a, b) => {
    const siteA = sites.value.find(s => s.id === parseInt(a));
    const siteB = sites.value.find(s => s.id === parseInt(b));
    return (siteA?.name || '').localeCompare(siteB?.name || '');
  });
  
  // Variables pour la pagination
  let pageNumber = 1;
  const totalPages = Object.keys(assignmentsBySite).reduce((total, siteId) => {
    // Estimer le nombre de pages par site (environ 25 agents par page)
    const agentsCount = assignmentsBySite[siteId].length;
    const pagesForSite = Math.ceil(agentsCount / 25);
    return total + pagesForSite;
  }, 0);
  
  // Fonction pour ajouter l'en-tête sur chaque page
  const addHeader = (siteName) => {
    doc.setFontSize(18);
    doc.setTextColor(38, 161, 148); // Couleur turquoise-deep
    doc.text('Assignations des Sites de Pointage', 14, 22);
    doc.setFontSize(14);
    doc.text(`Site: ${siteName || 'Non défini'}`, 14, 30);
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100); // Gris foncé
    doc.text(`Date d'exportation: ${new Date().toLocaleDateString('fr-FR')}`, 14, 37);
  };
  
  // Fonction pour ajouter le pied de page sur chaque page
  const addFooter = (currentPage) => {
    const pageSize = doc.internal.pageSize;
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
    
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150); // Gris clair
    
    // Légende à gauche
    doc.text('Generated by OpSolver', 14, pageSize.height - 10);
    
    // Pagination à droite
    doc.text(`Page ${currentPage} / ${totalPages}`, pageWidth - 30, pageSize.height - 10);
  };
  
  // Pour chaque site, créer le contenu
  sortedSiteIds.forEach((siteId, siteIndex) => {
    const siteAssignments = assignmentsBySite[siteId];
    const site = sites.value.find(s => s.id === parseInt(siteId));
    const siteName = site?.name || 'Non défini';
    
    // Si ce n'est pas le premier site, ajouter une nouvelle page
    if (siteIndex > 0) {
      doc.addPage();
    }
    
    // Préparer les données pour le tableau du site actuel
    const tableData = siteAssignments.map((assignment, index) => {
      // Récupérer le nom de la direction
      const directionName = assignment.agent?.direction_id ? 
        getDirectionName(assignment.agent.direction_id) : 'Non définie';
      
      return [
        index + 1, // Numéro d'ordre
        `${assignment.agent?.nom || ''} ${assignment.agent?.prenom || ''}`,
        assignment.agent?.sexe || '',
        assignment.agent?.grade || '',
        directionName
      ];
    });
    
    // Créer le tableau pour ce site avec en-tête sur chaque page
    autoTable(doc, {
      head: [['N°', 'Noms',  'Sexe','Grade', 'Direction']],
      body: tableData,
      startY: 41,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { 
        fillColor: [48, 199, 181], // Couleur turquoise-primary
        textColor: [255, 255, 255], 
        fontStyle: 'bold' 
      },
      alternateRowStyles: { fillColor: [240, 250, 250] },
      margin: { top: 41, bottom: 25 },
      // Ajouter l'en-tête et le pied de page sur chaque page
      didDrawPage: (data) => {
        // Ajouter l'en-tête
        addHeader(siteName);
        
        // Ajouter le pied de page
        addFooter(pageNumber);
        
        // Incrémenter le numéro de page si une nouvelle page est créée
        if (data.pageNumber < data.pageCount) {
          pageNumber++;
        }
      }
    });
    
    // Incrémenter le numéro de page pour la prochaine section
    pageNumber++;
  });
  
  // Sauvegarder le PDF
  doc.save('assignations_sites.pdf');
  
  showToast('success', 'Export réussi', 'Les données ont été exportées au format PDF.');
}

// Système de notifications toast
function showToast(type, title, message) {
  const icons = {
    success: 'check_circle',
    error: 'error',
    warning: 'warning',
    info: 'info'
  };
  
  const toast = {
    id: Date.now(),
    type,
    title,
    message,
    icon: icons[type] || 'info'
  };
  
  toasts.value.push(toast);
  
  // Supprimer le toast après 5 secondes
  setTimeout(() => {
    toasts.value = toasts.value.filter(t => t.id !== toast.id);
  }, 5000);
}

// Afficher un toast stylé avec animation
const showStyledToast = (type, title, message) => {
  // Créer l'élément toast
  const toastElement = document.createElement('div');
  toastElement.className = 'toast-success';
  
  // Marquer le toast comme actif (pour éviter les suppressions multiples)
  toastElement.dataset.active = 'true';
  
  // Créer le contenu du toast
  toastElement.innerHTML = `
    <span class="material-icons">${type === 'success' ? 'check_circle' : 'info'}</span>
    <div class="toast-content">
      <div class="toast-title">${title}</div>
      <div class="toast-message">${message}</div>
    </div>
    <button class="toast-close">
      <span class="material-icons">close</span>
    </button>
  `;
  
  // Ajouter le toast au document
  document.body.appendChild(toastElement);
  
  // Fonction de suppression sécurisée du toast
  const removeToastSafely = () => {
    // Vérifier si le toast est toujours actif et dans le DOM
    if (toastElement.dataset.active === 'true' && document.body.contains(toastElement)) {
      // Marquer comme inactif pour éviter les suppressions multiples
      toastElement.dataset.active = 'false';
      // Animation de disparition
      toastElement.style.opacity = '0';
      
      // Supprimer après l'animation
      setTimeout(() => {
        if (document.body.contains(toastElement)) {
          try {
            document.body.removeChild(toastElement);
          } catch (error) {
            console.log('Le toast a déjà été supprimé');
          }
        }
      }, 300);
    }
  };
  
  // Ajouter l'événement pour fermer le toast
  const closeButton = toastElement.querySelector('.toast-close');
  closeButton.addEventListener('click', removeToastSafely);
  
  // Supprimer automatiquement après 5 secondes
  setTimeout(removeToastSafely, 5000);
};
</script>